import React from "react";
import { useQuery } from "@tanstack/react-query";
import Navbar from "../components/Navbar";
import { MentoriaFilters } from "../components/mentoria/MentoriaFilters";
import { MentorshipList } from "../components/mentoria/MentorshipList";
import { useMentoriasPage } from "@/hooks/useMentoriasPage";
import { fetchBannersByPage } from "@/services/bannersService";
import { PageBanner } from "@/components/common/PageBanner";
import { useFeaturedItemsForPage } from "@/hooks/useFeaturedItems";
import { Crown, Trophy, Star } from "lucide-react";
import { MentorshipOfferingCard } from "@/components/mentor/MentorshipOffering";

const MentoriasPage = () => {
  const {
    mentorships,
    mentorRatings,
    isLoading,
    error,
    games,
    searchTerm,
    selectedGameId,
    selectedType,
    handleSearchChange,
    handleGameFilterChange,
    handleTypeFilterChange,
    handleCardClick,
  } = useMentoriasPage();

  // Query para buscar banners da página de mentorias
  const { data: banners } = useQuery({
    queryKey: ["banners", "mentorias"],
    queryFn: () => fetchBannersByPage("mentorias"),
  });

  // Query para buscar mentorias em destaque
  const { data: featuredMentorias } = useFeaturedItemsForPage('mentoria');

  // Separar mentorias em destaque das normais
  const featuredMentoriaIds = featuredMentorias?.map(item => item.item_id) || [];
  const featuredMentoriasList = mentorships.filter(mentoria => featuredMentoriaIds.includes(mentoria.id));
  const regularMentoriasList = mentorships.filter(mentoria => !featuredMentoriaIds.includes(mentoria.id));

  // Ordenar mentorias em destaque por posição
  const sortedFeaturedMentorias = featuredMentoriasList.sort((a, b) => {
    const positionA = featuredMentorias?.find(item => item.item_id === a.id)?.position || 999;
    const positionB = featuredMentorias?.find(item => item.item_id === b.id)?.position || 999;
    return positionA - positionB;
  });

  // Função para obter ícone de posição
  const getPositionIcon = (position: number) => {
    switch (position) {
      case 1: return <Crown className="h-4 w-4 text-yellow-500" />;
      case 2: return <Trophy className="h-4 w-4 text-gray-400" />;
      case 3: return <Star className="h-4 w-4 text-amber-600" />;
      default: return null;
    }
  };

  // Função para obter cor da borda de destaque
  const getFeaturedBorderClass = (position: number) => {
    switch (position) {
      case 1: return 'border-yellow-500 shadow-yellow-500/20';
      case 2: return 'border-gray-400 shadow-gray-400/20';
      case 3: return 'border-amber-600 shadow-amber-600/20';
      default: return '';
    }
  };

  return (
    <div className="bg-[#0f141c] min-h-screen pb-12">
      <Navbar />
      <div className="max-w-6xl mx-auto px-4">
        {/* Banner da página */}
        {banners && banners.length > 0 && (
          <PageBanner banners={banners} />
        )}

        <div className="pt-8">
          <h1 className="text-3xl font-extrabold text-[#00B6F0] mb-8 text-center">
            Mentorias Disponíveis
          </h1>
          
          <MentoriaFilters 
            searchTerm={searchTerm}
            selectedGameFilter={selectedGameId}
            selectedTypeFilter={selectedType}
            games={games}
            onSearchChange={handleSearchChange}
            onGameFilterChange={handleGameFilterChange}
            onTypeFilterChange={handleTypeFilterChange}
          />
          
          {/* Mentorias em Destaque */}
          {sortedFeaturedMentorias.length > 0 && (
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2">
                <Star className="h-6 w-6 text-[#00B6F0]" />
                Mentorias em Destaque
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortedFeaturedMentorias.map(mentoria => {
                  const featuredItem = featuredMentorias?.find(item => item.item_id === mentoria.id);
                  const position = featuredItem?.position || 0;
                  return (
                    <div key={mentoria.id} className="relative">
                      {/* Badge de posição */}
                      <div className="absolute -top-2 -right-2 z-30 bg-gradient-to-r from-[#00B6F0] to-[#0099CC] rounded-full p-2 shadow-lg">
                        {getPositionIcon(position)}
                      </div>
                      <div className={`border-2 ${getFeaturedBorderClass(position)} rounded-xl shadow-lg`}>
                        <MentorshipOfferingCard
                          id={mentoria.id}
                          title={mentoria.title}
                          description={mentoria.description}
                          price={mentoria.price_cents}
                          duration={mentoria.duration_minutes}
                          game={mentoria.game}
                          coverImageUrl={mentoria.cover_image_url}
                          isActive={mentoria.is_active}
                          isGroup={mentoria.is_group}
                          maxParticipants={mentoria.max_participants}
                          mentorName={mentoria.mentor_name}
                          mentorImageUrl={mentoria.mentor_image_url}
                          mentorId={mentoria.mentor_id}
                          onCardClick={() => {
                            if (mentoria.mentor_id) {
                              handleCardClick(mentoria.mentor_id, mentoria.mentor_slug);
                            }
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Outras Mentorias */}
          <div className="mb-8">
            {sortedFeaturedMentorias.length > 0 && (
              <h2 className="text-2xl font-bold text-white mb-4">
                Outras Mentorias
              </h2>
            )}
            <MentorshipList
              mentorships={regularMentoriasList}
              mentorRatings={mentorRatings}
              isLoading={isLoading}
              error={error}
              onCardClick={handleCardClick}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MentoriasPage;
